import React from 'react';
import { useParams, useLocation, Redirect } from 'react-router-dom';
import { usePrefetchSuspense } from 'src/lib/usePrefetch';
import slugify from 'slugify';
import dayjs from 'dayjs';
import advancedFormat from 'dayjs/plugin/advancedFormat';
dayjs.extend(advancedFormat);
// Components
import { CatalogPage } from 'src/pages/CatalogPage/CatalogPage';
import { UserItemContextProvider } from 'src/lib/UserItemContext';
import {
  TAuctionCollections,
  IAuctionMappings,
  ICatalogRouteParams,
  IAuctionData,
} from 'src/pages/CatalogPage/Catalog.i';
import { Page404 } from 'src/pages/Page404';
import { useFetch } from 'src/lib/FetchContext';

const Catalog = ():JSX.Element => {
  const { fetch } = useFetch();

  async function initializeMappings(auctionId: number): Promise<IAuctionMappings> {
    // redo mappings as an object with fields containing array

    const mappingsReq = await fetch(`/api/catalog/getMappings/${auctionId}`);
    const auctionMappings = await mappingsReq.json();

    const amCodeMappings = [
      { slug: '', value: 'All', name: 'All' },
      { slug: 'antique-firearms', value: 'A', name: 'Antique Firearms' },
      { slug: 'antique-lots', value: 'AL', name: 'Antique Lots' },
      { slug: 'curio-and-relic', value: 'C', name: 'Curio & Relic' },
      { slug: 'class-iii', value: 'I', name: 'Class III' },
      { slug: 'modern-firearms', value: 'M', name: 'Modern Firearms' },
    ];

    /**
     * Removed the following hloMappings to help clean up classifications
     * { slug: 'carbine', value: 'Carbine', name: 'Carbine' },
     * { slug: 'combination', value: 'Combination', name: 'Combination' },
     * { slug: 'musket', value: 'Musket', name: 'Musket' },
     */

    const hloMappings = [
      { slug: '', value: 'All', name: 'All' },
      { slug: 'handgun', value: 'H', name: 'Handgun' },
      { slug: 'pistol', value: 'Pistol', name: 'Pistol' },
      { slug: 'revolver', value: 'Revolver', name: 'Revolver' },
      { slug: 'long-gun', value: 'L', name: 'Long Gun' },
      { slug: 'rifle', value: 'Rifle', name: 'Rifle' },
      { slug: 'shotgun', value: 'Shotgun', name: 'Shotgun' },
      { slug: 'other', value: 'Other', name: 'Other' },
    ];

    const manufacturerMappings = auctionMappings.manufacturers.map((manu) => ({
      slug: slugify(manu.ManufacturerName || 'error-null-manufacturername').toLowerCase(),
      value: manu.ManufacturerName || 'error-null-manufacturername',
      name: manu.ManufacturerName || 'error-null-manufacturername',
    }));

    const caliberMappings = auctionMappings.calibers.caliber.map((cal) => ({
      slug: slugify(cal),
      value: cal,
      name: cal,
    }));

    const subcaliberMappings = auctionMappings.calibers.subcaliber.map((subcal) => ({
      // need to make sure subcal slug !== caliber slug
      slug: `s${slugify(subcal)}`,
      value: subcal,
      name: subcal,
    }));

    const sessionMappings = auctionMappings.sessions.map((session) => ({
      slug: `ses${session.AuctionSessionID}`,
      value: session.AuctionSessionID.toString(),
      name: dayjs(session.Date).format('dddd, MMM Do'), // DayJS Note: Interpreting a Chicago date in local time is fine.
      startingLot: session.StartingLot,
      endingLot: session.EndingLot,
    }));

    sessionMappings.unshift({ slug: '', value: 'All', name: 'All Days' });

    return {
      amcode: amCodeMappings,
      hlo: hloMappings,
      manu: manufacturerMappings,
      caliber: caliberMappings,
      subcaliber: subcaliberMappings,
      session: sessionMappings,
      hasBids: [{ slug: 'hasBids', value: 'false', name: 'No Bids' }],
    };
  }

  async function initializeCollections(auctionId: number): Promise<TAuctionCollections> {
    const collectionsReq = await fetch(`/api/catalog/collections/${auctionId}`);
    return await collectionsReq.json();
  }

  const parameters = useParams<ICatalogRouteParams>();
  const location = useLocation();

  // Set auctionID in context to fetch user text alerts and wishlists

  const params = [
    parameters.p0,
    parameters.p1,
    parameters.p2,
    parameters.p3,
    parameters.p4,
    parameters.p5,
  ].filter((param) => param);

  const auctionId = parseInt(params[0]);

  const auctionDataPromise = usePrefetchSuspense<IAuctionData>(`catalog-auction-data-${auctionId}`, async () => {
    if(auctionId === 0) return null;
    const auctionDataReq = await fetch(`/api/catalog/initAuctionData/${auctionId}`);
    const auctionDataRes: { AuctionData: IAuctionData } = await auctionDataReq.json();
    if(auctionDataRes.AuctionData) {
      return auctionDataRes.AuctionData;
    }
    else return auctionDataRes as unknown as IAuctionData;
  });

  // Mappings
  const auctionMappingsCollectionsPromise = usePrefetchSuspense<
  {mappings: IAuctionMappings, collections: TAuctionCollections}
  >(`catalog-auction-mappings-${auctionId}`, async () => {
    if(auctionId === 0) return null;
    const promises = await Promise.all([
      await initializeMappings(auctionId),
      await initializeCollections(auctionId),
    ]);
    return { mappings: promises[0], collections: promises[1] };
  });

  const auctionData = auctionDataPromise.read();
  const auctionMappingsCollections = auctionMappingsCollectionsPromise.read();

  if(!auctionData || !auctionMappingsCollections?.mappings || !auctionData.AuctionID) {
    // invalid auction ID or other error
    console.log(auctionId, auctionData);
    return <Page404/>;
  }

  return (
    <UserItemContextProvider auctionId={auctionId}>
      <CatalogPage
        auctionId={auctionId}
        auctionData={auctionData}
        mappings={auctionMappingsCollections?.mappings}
        // STUB: trying to prevent crash, but this conditional might lead to problems
        collections={auctionMappingsCollections?.collections || []}
      />
    </UserItemContextProvider>
  );
};

export default Catalog;
